import { CourseTreeNodeTypeEnum, type CourseTreeNode } from "@/types/generated";
import { storyboardTitle } from "@/lib/storyboardTitle";
import { listSubChapterStoryboards } from "@/api/storyboards";
import type { LoadedCourse } from "@/stores/courseDetailStore";
import type { Storyboard, TextFlashcardsData } from "@/types/storyboard";

export interface Deck {
  id: string;
  moduleTitle: string;
  subchapterTitle: string;
  deckTitle: string;
  totalCards: number;
  introduction: string[];
  instruction?: string;
  cards: { frontside: string; backside: string }[];
}

const collectSubchapters = (node: CourseTreeNode): CourseTreeNode[] => {
  if (node.type === CourseTreeNodeTypeEnum.Subchapter) return [node];
  return (node.children ?? []).flatMap(collectSubchapters);
};

const collectContentElementIds = (node: CourseTreeNode): string[] => {
  if (node.type === CourseTreeNodeTypeEnum.ContentElement) return [node.id];
  return (node.children ?? []).flatMap(collectContentElementIds);
};

const findContentElement = (
  node: CourseTreeNode,
  id: string
): CourseTreeNode | null => {
  if (node.id === id) return node;
  for (const child of node.children ?? []) {
    const found = findContentElement(child, id);
    if (found) return found;
  }
  return null;
};

const isFlashcardStoryboard = (
  s: Storyboard
): s is Storyboard & {
  contentType: "TEXT";
  subType: "FLASHCARDS";
  text: TextFlashcardsData;
} => s.contentType === "TEXT" && s.subType === "FLASHCARDS";

const toCompletedSet = (
  completedContentElementIds: ReadonlySet<string> | readonly string[]
): ReadonlySet<string> =>
  completedContentElementIds instanceof Set
    ? completedContentElementIds
    : new Set(completedContentElementIds);

/**
 * Collects Lernkarten decks whose content elements are marked completed
 * (flip-all in the lesson, or historically completed LearningProgress).
 */
export const collectDecks = async (
  courseStructure: Pick<LoadedCourse, "structure">,
  completedContentElementIds: ReadonlySet<string> | readonly string[]
): Promise<Deck[]> => {
  const completed = toCompletedSet(completedContentElementIds);
  if (completed.size === 0) {
    return [];
  }

  const modules = courseStructure.structure.filter(
    (n) => n.type === CourseTreeNodeTypeEnum.Module
  );

  const decks: Deck[] = [];

  for (const moduleNode of modules) {
    const subchapters = collectSubchapters(moduleNode).filter((subchapter) =>
      collectContentElementIds(subchapter).some((id) => completed.has(id))
    );
    if (subchapters.length === 0) {
      continue;
    }

    const storyboardLists = await Promise.all(
      subchapters.map((sc) =>
        // A subchapter whose storyboards cannot be loaded adds no cards.
        listSubChapterStoryboards(sc.id)
          .catch((): Storyboard[] => [])
          .then((sbs) => ({ subchapter: sc, storyboards: sbs }))
      )
    );

    for (const { subchapter, storyboards } of storyboardLists) {
      for (const sb of storyboards) {
        if (!isFlashcardStoryboard(sb)) continue;
        if (!completed.has(sb.contentElementId)) continue;
        const data = sb.text;
        if (!data?.cards?.length) continue;

        const ce = findContentElement(moduleNode, sb.contentElementId);
        decks.push({
          id: sb.contentElementId,
          moduleTitle: moduleNode.title,
          subchapterTitle: subchapter.title,
          deckTitle: storyboardTitle(data.title, ce?.title || subchapter.title),
          totalCards: data.cards.length,
          introduction: data.introduction ?? [],
          instruction: data.instruction,
          cards: data.cards,
        });
      }
    }
  }

  return decks;
};
